import { useState, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { getGraphClient } from "../services/graphClient";
import { getThumbnailUrl } from "../services/sharepoint";

const STATUS_OPTIONS = ["Ordered", "Partially Received", "Received", "Closed / Invoiced"];
const STATUS_CLASSES = {
  "Ordered":             "pill-ordered",
  "Received":            "pill-received",
  "Partially Received":  "pill-partial",
  "Closed / Invoiced":   "pill-closed",
};

function StatusPill({ status, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const current = status || "Ordered";
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block" }} onClick={e => e.stopPropagation()}>
      <span
        className={`pill ${STATUS_CLASSES[current] || "pill-ordered"}`}
        style={{ cursor: "pointer", userSelect: "none", fontSize: 11 }}
        onClick={() => setOpen(o => !o)}
      >
        {current}
        <i className="ti ti-chevron-down" style={{ fontSize: 10, marginLeft: 3, verticalAlign: -1 }} aria-hidden="true" />
      </span>
      {open && (
        <div className="status-dropdown" onClick={e => e.stopPropagation()}>
          {STATUS_OPTIONS.map(s => (
            <div
              key={s}
              className={`status-option${s === current ? " active" : ""}`}
              onClick={() => { onChange && onChange(s); setOpen(false); }}
            >
              <span className={`pill ${STATUS_CLASSES[s]}`}>{s}</span>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

function POThumbnail({ po, selected, driveId, onSelect, onDoubleClick, onMove, onStatusChange }) {
  const { instance, accounts } = useMsal();
  const [thumbUrl, setThumbUrl] = useState(null);
  const [thumbError, setThumbError] = useState(false);

  useEffect(() => {
    if (!driveId || !po.id) return;
    let cancelled = false;
    (async () => {
      try {
        const client = getGraphClient(instance, accounts[0]);
        const url = await getThumbnailUrl(client, driveId, po.id);
        if (!cancelled) setThumbUrl(url);
      } catch {
        if (!cancelled) setThumbError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [po.id, driveId, instance, accounts]);

  const status = po.fields?.Status || "Ordered";

  return (
    <div
      className={`po-card${selected ? " selected" : ""}`}
      onClick={() => onSelect(po)}
      onDoubleClick={() => onDoubleClick(po)}
      title={`Double-click to open ${po.name} in SharePoint`}
    >
      <div className="po-card-thumb">
        {thumbUrl && !thumbError ? (
          <img src={thumbUrl} alt={`Preview of ${po.name}`} className="po-card-img" onError={() => setThumbError(true)} />
        ) : (
          <div className="po-card-icon"><i className="ti ti-file-type-pdf" aria-hidden="true" /></div>
        )}
      </div>
      <div className="po-card-footer">
        <div className="po-card-name" title={po.name}>{po.name}</div>
        <div className="po-card-meta">
          <StatusPill status={status} onChange={s => onStatusChange && onStatusChange(po.id, s)} />
          <button className="icon-btn" title="Move to another vendor" onClick={e => { e.stopPropagation(); onMove(po); }} aria-label="Move document">
            <i className="ti ti-folder-symlink" style={{ fontSize: 14 }} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function POGrid({ pos, loading, selectedPO, driveId, onSelect, onMove, onStatusChange, searchQuery, isSearchResults }) {
  const filtered = searchQuery ? pos.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase())) : pos;

  if (loading) return <div className="state-box"><div className="spinner" /><p>Loading purchase orders…</p></div>;
  if (!loading && pos.length === 0) return <div className="state-box"><i className={isSearchResults ? "ti ti-search-off" : "ti ti-folder-open"} aria-hidden="true" /><p>{isSearchResults ? "No matching documents" : "No PDFs in this folder"}</p></div>;
  if (filtered.length === 0) return <div className="state-box"><i className="ti ti-search" aria-hidden="true" /><p>No results for "{searchQuery}"</p></div>;

  return (
    <div className="po-grid">
      {filtered.map(po => (
        <POThumbnail key={po.id} po={po} selected={selectedPO?.id === po.id} driveId={driveId}
          onSelect={onSelect} onDoubleClick={po => window.open(po.webUrl, "_blank")}
          onMove={onMove} onStatusChange={onStatusChange} />
      ))}
    </div>
  );
}
